import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import {
  FuelReading,
  fuelErrorMessage,
  fuelErrorStatus,
} from '../../../services/fuelReadingsService';
import {
  SLOT_LABEL,
  formatDateTime,
  formatLiters,
  formatReading,
  parseReading,
} from '../../../utils/fuelReadings';

const REASON_MIN = 5;

// supervisor ou ti corrige o número lançado errado (ex.: sem a vírgula). o
// número antigo fica no histórico e a leitura seguinte é recalculada.
export function EditReadingDialog({
  reading,
  onClose,
  onConfirm,
}: {
  reading: FuelReading | null;
  onClose: () => void;
  onConfirm: (data: {
    reading: string;
    reason: string;
    confirm_high_jump?: boolean;
  }) => Promise<unknown>;
}) {
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // acima do limite da bomba: a api pede confirmação (409)
  const [confirmMessage, setConfirmMessage] = useState('');

  useEffect(() => {
    if (reading) {
      setValue('');
      setReason('');
      setError('');
      setConfirmMessage('');
    }
  }, [reading]);

  const number = parseReading(value);
  const valid = Number.isFinite(number) && number > 0;
  const liters =
    valid && reading && reading.previous_reading !== null
      ? number - reading.previous_reading
      : null;

  const save = async (confirm: boolean) => {
    setSaving(true);
    setError('');
    try {
      await onConfirm({
        reading: value.trim(),
        reason: reason.trim(),
        confirm_high_jump: confirm || undefined,
      });
      onClose();
    } catch (err) {
      const message = fuelErrorMessage(err, 'Não foi possível corrigir.');

      if (fuelErrorStatus(err) === 409) {
        setConfirmMessage(message);
      } else {
        setConfirmMessage('');
        setError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={!!reading} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent bg="gray.700" mx="4">
        <ModalHeader>Corrigir leitura</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {reading && (
            <Text>
              {SLOT_LABEL[reading.slot]} da {reading.pump_name} em{' '}
              {formatDateTime(reading.read_at, true)}: lançado{' '}
              <b>{formatReading(reading.reading)}</b>
              {reading.previous_reading !== null && (
                <> (anterior: {formatReading(reading.previous_reading)})</>
              )}
              .
            </Text>
          )}

          <Text fontSize="sm" mt="4" mb="1">
            Número certo do totalizador
          </Text>
          <Input
            value={value}
            onChange={event => {
              setValue(event.target.value);
              setConfirmMessage('');
            }}
            inputMode="decimal"
            maxLength={20}
            fontFamily="mono"
            placeholder="Ex.: 220.486,1"
            aria-label="Número certo do totalizador"
          />
          <Text fontSize="xs" color="gray.500" mt="1">
            Digite com a vírgula das casas decimais, como aparece no visor.
            {liters !== null &&
              ` Vai ficar ${formatLiters(liters)} L desde a anterior.`}
          </Text>

          <Text fontSize="sm" mt="4" mb="1">
            Motivo da correção
          </Text>
          <Textarea
            value={reason}
            onChange={event => setReason(event.target.value)}
            maxLength={300}
            rows={3}
            placeholder="Ex.: lançado sem a vírgula (217.974,3 virou 2.179.743)"
            aria-label="Motivo da correção"
          />
          <Text fontSize="xs" color="gray.500" mt="1">
            O número lançado continua no histórico, com quem corrigiu e o
            motivo. A leitura seguinte da bomba é recalculada.
          </Text>

          {confirmMessage && (
            <Alert status="warning" color="orange.900" borderRadius="md" mt="3">
              <AlertIcon />
              <AlertDescription>{confirmMessage}</AlertDescription>
            </Alert>
          )}
          {error && (
            <Alert status="error" color="red.900" borderRadius="md" mt="3">
              <AlertIcon />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr="3" onClick={onClose}>
            Voltar
          </Button>
          <Button
            colorScheme={confirmMessage ? 'orange' : 'blue'}
            onClick={() => save(!!confirmMessage)}
            isLoading={saving}
            isDisabled={!valid || reason.trim().length < REASON_MIN}
          >
            {confirmMessage ? 'Está certo, salvar' : 'Corrigir'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
